import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Pagination,
} from '@mui/material';
import { PersonAdd, Search as SearchIcon, Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';

const ClientManagement = ({ patients, onUpdate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [allClients, setAllClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [dialogMode, setDialogMode] = useState('assign'); // 'assign' or 'create'
  const [page, setPage] = useState(1);
  const [newClientData, setNewClientData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const itemsPerPage = 10;

  // Filter clients based on table search query
  const filteredPatients = patients.filter((patient) => {
    if (!tableSearchQuery.trim()) return true;
    const query = tableSearchQuery.toLowerCase();
    return (
      patient.firstName?.toLowerCase().includes(query) ||
      patient.lastName?.toLowerCase().includes(query) ||
      patient.email?.toLowerCase().includes(query) ||
      patient.phone?.toLowerCase().includes(query) ||
      patient.temporaryPassword?.toLowerCase().includes(query)
    );
  });

  // Reset to first page when table search changes
  useEffect(() => {
    setPage(1);
  }, [tableSearchQuery]);

  const fetchAvailableClients = async (currentPatients = null) => {
    try {
      // Fetch unassigned clients from practitioner endpoint
      const response = await api.get('/practitioner/available-patients');
      // Use currentPatients if provided, otherwise use the patients prop
      const patientsToFilter = currentPatients || patients;
      const unassignedClients = response.data.filter(
        (c) => !patientsToFilter.some((ac) => ac._id === c._id)
      );
      setAllClients(unassignedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };


  const handleOpenDialog = async () => {
    try {
      await fetchAvailableClients();
      setSearchQuery('');
      setDialogMode('assign');
      setSelectedClientId('');
      setNewClientData({ firstName: '', lastName: '', email: '', phone: '' });
      setDialogOpen(true);
    } catch (error) {
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch clients',
        confirmButtonColor: '#1976d2',
      });
    }
  };

  // Filter clients based on search query
  const filteredClients = allClients.filter((client) => {
    const query = searchQuery.toLowerCase();
    return (
      client.firstName.toLowerCase().includes(query) ||
      client.lastName.toLowerCase().includes(query) ||
      client.email.toLowerCase().includes(query) ||
      (client.phone && client.phone.includes(query))
    );
  });

  const handleAssign = async () => {
    try {
      await api.post(`/practitioner/assign-patient/${selectedClientId}`);
      SwalWithA11yFix({
        icon: 'success',
        title: 'Success!',
        text: 'Client assigned successfully',
        timer: 2000,
        showConfirmButton: false,
      });
      setDialogOpen(false);
      setSelectedClientId('');
      if (onUpdate) onUpdate();
    } catch (err) {
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Assignment failed',
        confirmButtonColor: '#1976d2',
      });
    }
  };

  const handleRemoveClient = async (clientId, clientName) => {
    const result = await SwalWithA11yFix({
      title: 'Unassign Client?',
      text: `Are you sure you want to unassign ${clientName} from your clients?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#1976d2',
      confirmButtonText: 'Yes, unassign',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/practitioner/unassign-patient/${clientId}`);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Unassigned!',
          text: 'Client has been unassigned successfully',
          timer: 2000,
          showConfirmButton: false,
        });
        // Refresh the patients list and available clients
        if (onUpdate) {
          await onUpdate();
        }
        // Refresh available clients list
        await fetchAvailableClients();
      } catch (err) {
        // Handle errors with detailed messages
        let errorMessage = 'Failed to unassign client';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        console.error('Remove client error:', err);
        SwalWithA11yFix({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonColor: '#1976d2',
        });
      }
    }
  };

  const handleCreateClient = async () => {
    // Validate form
    if (!newClientData.firstName || !newClientData.lastName || !newClientData.email || !newClientData.phone) {
      SwalWithA11yFix({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please fill in all required fields',
        confirmButtonColor: '#1976d2',
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newClientData.email)) {
      SwalWithA11yFix({
        icon: 'warning',
        title: 'Invalid Email',
        text: 'Please enter a valid email address',
        confirmButtonColor: '#1976d2',
      });
      return;
    }

    try {
      const response = await api.post('/practitioner/create-patient', newClientData);
      const temporaryPassword = response.data?.temporaryPassword || response.data?.patient?.temporaryPassword;
      SwalWithA11yFix({
        icon: 'success',
        title: 'Success!',
        html: `Client created and assigned successfully.<br/><br/><strong>Password:</strong> ${temporaryPassword || 'Please refresh and check table'}`,
        confirmButtonColor: '#1976d2',
      });
      setDialogOpen(false);
      setNewClientData({ firstName: '', lastName: '', email: '', phone: '' });
      // Refresh the patients list first
      if (onUpdate) {
        await onUpdate();
        // Wait a bit for the component to update with new patients
        // Then fetch the updated available clients list
        setTimeout(async () => {
          // Fetch fresh patients list to use for filtering
          try {
            const response = await api.get('/practitioner/patients');
            await fetchAvailableClients(response.data);
          } catch (err) {
            console.error('Error refreshing available clients:', err);
          }
        }, 100);
      }
    } catch (err) {
      // Handle validation errors
      let errorMessage = 'Failed to create client';
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMessage = err.response.data.errors.map(e => e.msg || e.message).join('\n');
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      console.error('Create client error:', err);
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: '#1976d2',
      });
    }
  };

  return (
    <Box>

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h5">My Clients</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            placeholder="Search clients..."
            variant="outlined"
            size="small"
            value={tableSearchQuery}
            onChange={(e) => setTableSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />,
            }}
            sx={{
              width: { xs: '100%', sm: 250 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleOpenDialog}
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              },
            }}
          >
            Assign Client
          </Button>
        </Box>
      </Box>

      <TableContainer 
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Password</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No clients assigned yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPatients.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((client) => (
                <TableRow key={client._id}>
                  <TableCell>
                    {client.firstName} {client.lastName}
                  </TableCell>
                  <TableCell>{client.email}</TableCell>
                  <TableCell>{client.phone}</TableCell>
                  <TableCell>{client.temporaryPassword || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={client.isActive ? 'Active' : 'Inactive'}
                      color={client.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      color="error"
                      size="medium"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleRemoveClient(client._id, `${client.firstName} ${client.lastName}`)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 600,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        },
                      }}
                    >
                      Unassign
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {patients.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredPatients.length / itemsPerPage)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Assign/Create Client Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          setDialogOpen(false);
          setSearchQuery('');
          setSelectedClientId('');
          setNewClientData({ firstName: '', lastName: '', email: '', phone: '' });
        }} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2', pb: 1 }}>
          {dialogMode === 'assign' ? 'Assign Client' : 'Create New Client'}
        </DialogTitle>
        
        {/* Tabs for switching between assign and create */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs 
            value={dialogMode === 'assign' ? 0 : 1} 
            onChange={async (e, newValue) => {
              setDialogMode(newValue === 0 ? 'assign' : 'create');
              setSelectedClientId('');
              setSearchQuery('');
              setNewClientData({ firstName: '', lastName: '', email: '', phone: '' });
              // Refresh available clients when switching to "Assign Existing" tab
              if (newValue === 0) {
                // Fetch fresh patients list to ensure we have the latest data
                try {
                  const response = await api.get('/practitioner/patients');
                  await fetchAvailableClients(response.data);
                } catch (err) {
                  console.error('Error refreshing available clients:', err);
                  // Fallback to using current patients prop
                  await fetchAvailableClients();
                }
              }
            }}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
              }
            }}
          >
            <Tab label="Assign" />
            <Tab label="Create New" icon={<AddIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent sx={{ mt: 2 }}>
          {dialogMode === 'assign' ? (
            <>
              {/* Search Field */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <SearchIcon 
                    sx={{ 
                      position: 'absolute', 
                      left: 12, 
                      color: '#666',
                      fontSize: 20 
                    }} 
                  />
                  <TextField
                    fullWidth
                    placeholder="Search by name, email, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        paddingLeft: '0px',
                        '& input': {
                          paddingLeft: 4
                        }
                      }
                    }}
                    InputProps={{
                      endAdornment: searchQuery && (
                        <CloseIcon
                          onClick={() => setSearchQuery('')}
                          sx={{
                            cursor: 'pointer',
                            color: '#999',
                            fontSize: 18,
                            mr: 1,
                            '&:hover': {
                              color: '#333',
                            }
                          }}
                        />
                      ),
                    }}
                  />
                </Box>
              </Box>

              {/* Client Selection */}
              <FormControl fullWidth margin="normal">
                <InputLabel>Select Client</InputLabel>
                <Select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  label="Select Client"
                >
                  {filteredClients.length === 0 ? (
                    <MenuItem disabled>
                      {searchQuery ? 'No clients match your search' : 'No unassigned clients available'}
                    </MenuItem>
                  ) : (
                    filteredClients.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {client.firstName} {client.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.email} • {client.phone}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              
              {filteredClients.length === 0 && searchQuery && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  No clients found matching "{searchQuery}"
                </Typography>
              )}
            </>
          ) : (
            <>
              {/* Create New Client Form */}
              <TextField
                fullWidth
                label="First Name"
                value={newClientData.firstName}
                onChange={(e) => setNewClientData({ ...newClientData, firstName: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={newClientData.lastName}
                onChange={(e) => setNewClientData({ ...newClientData, lastName: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newClientData.email}
                onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Phone"
                value={newClientData.phone}
                onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                A password will be generated automatically. The client can reset it when they first log in.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDialogOpen(false);
              setSearchQuery('');
              setSelectedClientId('');
              setNewClientData({ firstName: '', lastName: '', email: '', phone: '' });
            }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={dialogMode === 'assign' ? handleAssign : handleCreateClient} 
            variant="contained" 
            disabled={dialogMode === 'assign' ? !selectedClientId : !newClientData.firstName || !newClientData.lastName || !newClientData.email || !newClientData.phone}
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                backgroundColor: '#bdbdbd',
              },
            }}
          >
            {dialogMode === 'assign' ? 'Assign' : 'Create & Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientManagement;

